import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function OrderTracking() {
  const { orderNo } = useParams();
  const [q, setQ] = useState(orderNo || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (code) => {
    if (!code) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/track/${code}`);
      setOrder(data);
    } catch { toast.error("Order not found"); setOrder(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (orderNo) track(orderNo); }, [orderNo]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="track-page">
      <p className="label-xs opacity-60">Order</p>
      <h1 className="mt-3 text-5xl font-serif tracking-tighter">Track Your Order</h1>
      <div className="mt-8 flex gap-3">
        <Input value={q} onChange={(e)=>setQ(e.target.value.toUpperCase())} placeholder="Enter Order ID e.g. TF260215XYZ12" className="rounded-none" data-testid="track-input"/>
        <Button onClick={() => track(q)} disabled={loading} className="rounded-none label-xs px-8" data-testid="track-btn">Track</Button>
      </div>

      {order && (
        <div className="mt-12 border p-8" data-testid="order-details">
          <div className="flex flex-wrap justify-between gap-3 mb-6">
            <div>
              <p className="font-serif text-2xl">{order.order_no}</p>
              <p className="text-xs opacity-60">{new Date(order.created_at).toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="font-body">{money(order.total)}</p>
              <p className="label-xs opacity-60">{order.payment_method === "cod" ? "Cash on Delivery" : "Razorpay"}</p>
            </div>
          </div>
          {/* Timeline */}
          <div className="border-t pt-6 space-y-4">
            {order.tracking_steps.map((s) => (
              <div key={s.stage} className="flex items-center gap-3">
                {s.done ? <CheckCircle2 className="h-5 w-5"/> : <Circle className="h-5 w-5 opacity-40"/>}
                <div className={s.done ? "" : "opacity-40"}>
                  <p className="font-body">{s.label}</p>
                  {s.at && <p className="text-xs opacity-60">{new Date(s.at).toLocaleString()}</p>}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t mt-6 pt-6">
            <p className="label-xs mb-3">Items</p>
            {order.items.map((i, idx) => (
              <div key={idx} className="flex gap-3 py-2">
                <img src={i.image} alt={i.name} className="w-12 h-14 object-cover" />
                <div className="flex-1">
                  <p className="text-sm">{i.name}</p>
                  <p className="text-xs opacity-60">Size {i.size} · Qty {i.qty}</p>
                </div>
                <div className="text-sm">{money(i.price * i.qty)}</div>
              </div>
            ))}
          </div>
          <div className="border-t mt-6 pt-4 text-sm opacity-80">
            <p><span className="opacity-60">Shipping to:</span> {order.address.full_name}, {order.address.line1}, {order.address.city}, {order.address.state} {order.address.pincode}</p>
          </div>
        </div>
      )}
    </div>
  );
}
